import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TextField, Select, Row, Col, Label, MessageBanner } from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';

function CronBuilder({ input, meta, label }) {
  const intl = useIntl();
  const [minute, setMinute] = useState('0');
  const [hour, setHour] = useState('9');
  const [dayOfMonth, setDayOfMonth] = useState('*');
  const [month, setMonth] = useState('*');
  const [dayOfWeek, setDayOfWeek] = useState('?');
  const [cronExpression, setCronExpression] = useState('0 0 9 * * ?');
  const [nextRunPreview, setNextRunPreview] = useState(null);

  const minuteOptions = Array.from({ length: 60 }, (_, i) => ({
    value: i.toString(),
    label: i.toString().padStart(2, '0')
  }));

  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i.toString(),
    label: i.toString().padStart(2, '0')
  }));

  const dayOfWeekOptions = [
    { value: '?',   label: intl.formatMessage({ id: 'ui-reports.cronBuilder.dayOfWeek.any' }) },
    { value: 'MON', label: intl.formatMessage({ id: 'ui-reports.cronBuilder.dayOfWeek.MON' }) },
    { value: 'TUE', label: intl.formatMessage({ id: 'ui-reports.cronBuilder.dayOfWeek.TUE' }) },
    { value: 'WED', label: intl.formatMessage({ id: 'ui-reports.cronBuilder.dayOfWeek.WED' }) },
    { value: 'THU', label: intl.formatMessage({ id: 'ui-reports.cronBuilder.dayOfWeek.THU' }) },
    { value: 'FRI', label: intl.formatMessage({ id: 'ui-reports.cronBuilder.dayOfWeek.FRI' }) },
    { value: 'SAT', label: intl.formatMessage({ id: 'ui-reports.cronBuilder.dayOfWeek.SAT' }) },
    { value: 'SUN', label: intl.formatMessage({ id: 'ui-reports.cronBuilder.dayOfWeek.SUN' }) },
  ];

  const presets = [
    { value: '0 0 9 * * ?',     label: intl.formatMessage({ id: 'ui-reports.cronBuilder.preset.dailyAt9' }) },
    { value: '0 0 9 ? * MON',   label: intl.formatMessage({ id: 'ui-reports.cronBuilder.preset.everyMonday' }) },
    { value: '0 0 9 1 * ?',     label: intl.formatMessage({ id: 'ui-reports.cronBuilder.preset.firstDayMonth' }) },
    { value: '0 0 * * * ?',     label: intl.formatMessage({ id: 'ui-reports.cronBuilder.preset.everyHour' }) },
    { value: '0 */30 * * * ?',  label: intl.formatMessage({ id: 'ui-reports.cronBuilder.preset.every30min' }) },
  ];

  const customLabel = intl.formatMessage({ id: 'ui-reports.cronBuilder.preset.custom' });

  useEffect(() => {
    const cron = `0 ${minute} ${hour} ${dayOfMonth} ${month} ${dayOfWeek}`;
    setCronExpression(cron);
    if (input.onChange) input.onChange(cron);
    computeNextRun(cron);
  }, [minute, hour, dayOfMonth, month, dayOfWeek, input]);

  const computeNextRun = (cron) => {
    try {
      // Simple client-side next-run approximation
      const parts = cron.trim().split(/\s+/);
      if (parts.length < 6) { setNextRunPreview(null); return; }
      const h = parseInt(parts[2], 10);
      const m = parseInt(parts[1], 10);
      if (isNaN(h) || isNaN(m)) { setNextRunPreview(null); return; }
      const now = new Date();
      const next = new Date();
      next.setHours(h, m, 0, 0);
      if (next <= now) next.setDate(next.getDate() + 1);
      setNextRunPreview(next.toLocaleString());
    } catch (e) {
      setNextRunPreview(null);
    }
  };

  const handlePresetChange = (e) => {
    const preset = e.target.value;
    if (preset) {
      setCronExpression(preset);
      if (input.onChange) input.onChange(preset);
      computeNextRun(preset);
      const parts = preset.split(' ');
      if (parts.length >= 6) {
        setMinute(parts[1]);
        setHour(parts[2]);
        setDayOfMonth(parts[3]);
        setMonth(parts[4]);
        setDayOfWeek(parts[5]);
      }
    }
  };

  return (
    <div>
      {label && <Label>{label}</Label>}
      
      <Row>
        <Col xs={12}>
          <Select
            label={<FormattedMessage id="ui-reports.cronBuilder.presets" defaultMessage="Presets" />}
            dataOptions={[{ value: '', label: customLabel }, ...presets]}
            onChange={handlePresetChange}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={3}>
          <Select
            label={<FormattedMessage id="ui-reports.cronBuilder.hour" defaultMessage="Hour" />}
            dataOptions={hourOptions}
            value={hour}
            onChange={(e) => setHour(e.target.value)}
          />
        </Col>
        <Col xs={3}>
          <Select
            label={<FormattedMessage id="ui-reports.cronBuilder.minute" defaultMessage="Minute" />}
            dataOptions={minuteOptions}
            value={minute}
            onChange={(e) => setMinute(e.target.value)}
          />
        </Col>
        <Col xs={6}>
          <Select
            label={<FormattedMessage id="ui-reports.cronBuilder.dayOfWeek" defaultMessage="Day of Week" />}
            dataOptions={dayOfWeekOptions}
            value={dayOfWeek}
            onChange={(e) => {
              setDayOfWeek(e.target.value);
              if (e.target.value !== '?') {
                setDayOfMonth('?');
              }
            }}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <TextField
            label={<FormattedMessage id="ui-reports.cronBuilder.expression" defaultMessage="Cron Expression" />}
            value={cronExpression}
            onChange={(e) => {
              setCronExpression(e.target.value);
              if (input.onChange) {
                input.onChange(e.target.value);
              }
            }}
            readOnly={false}
            fullWidth
          />
        </Col>
      </Row>

      {meta.touched && meta.error && (
        <div style={{ color: 'red', fontSize: '0.85rem', marginTop: '0.25rem' }}>
          {meta.error}
        </div>
      )}

      <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
        <FormattedMessage id="ui-reports.cronBuilder.help" />
      </div>

      {nextRunPreview && (
        <MessageBanner type="informational" style={{ marginTop: '0.75rem' }}>
          <FormattedMessage
            id="ui-reports.cronBuilder.nextRun"
            defaultMessage="Next execution: {time}"
            values={{ time: nextRunPreview }}
          />
        </MessageBanner>
      )}
    </div>
  );
}

CronBuilder.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  label: PropTypes.node
};

export default CronBuilder;
